import React, {
    createContext,
    CSSProperties,
    FC,
    PropsWithChildren,
    ReactNode,
    TdHTMLAttributes,
    useEffect,
    useRef,
    useState,
} from 'react'
import { concat, get } from 'lodash'
import useFormInstance from 'antd/es/form/hooks/useFormInstance'
import { NamePath } from 'antd/es/form/interface'
import { EditableCellValue, TdStyled } from './elements'
import { CellValidator } from '../../helpers/cell-validators'
import { useCellValidation } from '../../hooks/use-cell-validation'
import { ValidationTooltip } from '../validation-tooltip/validation-tooltip'

export const EditableCellCtx = createContext<{ onBlur: () => void; onCancel: () => void }>({
    onBlur: () => {},
    onCancel: () => {},
})

export interface FieldDescriptors {
    name: NamePath
    getValue: (record: any) => any
}

interface EditableCellProps {
    title: ReactNode
    editing: boolean
    editable: boolean
    FormItem: ReactNode
    dataIndex: keyof {}
    record: any
    handleSave: (record: any, formValues: any) => Promise<void>
    onClickByEditableCell: () => void
    relationPath?: NamePath
    validator?: CellValidator
    fieldDescriptors?: FieldDescriptors[]
    /**
     * Опциональный колбэк для страниц, которым нужно знать о начале/конце
     * сессии редактирования конкретной записи (например, чтобы блокировать
     * действия над ней, пока не завершится сохранение) — EditableCell общий
     * для нескольких таблиц (расчёты, отгрузки, приходы), поэтому сам он
     * ничего никуда не диспатчит и не знает про конкретный Redux-слайс.
     */
    onEditingSessionChange?: (documentId: string, active: boolean) => void
}

export const EditableCell: FC<PropsWithChildren<EditableCellProps>> = React.memo(
    ({
        title,
        editable,
        children,
        FormItem,
        dataIndex,
        record,
        handleSave,
        relationPath,
        validator,
        fieldDescriptors,
        onEditingSessionChange,
        ...restProps
    }) => {
        const { style: cellStyle, ...cellRest } = restProps as TdHTMLAttributes<HTMLTableCellElement> &
            Record<string, unknown>
        const [editing, setEditing] = useState(false)
        const form = useFormInstance()
        const validationResult = useCellValidation(validator, record)

        const editingRef = useRef(editing)
        editingRef.current = editing
        const positionIdRef = useRef(record?.documentId)
        positionIdRef.current = record?.documentId
        const onEditingSessionChangeRef = useRef(onEditingSessionChange)
        onEditingSessionChangeRef.current = onEditingSessionChange

        useEffect(() => {
            return () => {
                if (editingRef.current && positionIdRef.current) {
                    onEditingSessionChangeRef.current?.(positionIdRef.current, false)
                }
            }
        }, [])

        const validationBg = validationResult?.bgColor
        const mergedCellStyle: CSSProperties | undefined = validationBg
            ? {
                  ...(cellStyle ?? {}),
                  backgroundColor: validationBg,
                  ['--cell-validation-bg' as string]: validationBg,
              }
            : cellStyle

        const getName = () => {
            if (relationPath) {
                return concat(relationPath, 'set')
            } else {
                return dataIndex
            }
        }

        const getValue = () => {
            if (relationPath) {
                const relation = get(record, relationPath)

                if (Array.isArray(relation)) {
                    return relation.map(item => item['documentId'])
                }

                if (relation) {
                    return relation['documentId']
                }
            } else {
                return Array.isArray(dataIndex) ? get(record, dataIndex) : record[dataIndex]
            }
        }

        const handleSaveOfSimpleField = () => {
            const name = getName()
            const value = form.getFieldValue(name)
            const savePromise = handleSave(record, [name, value])
            form.resetFields([name])
            return savePromise
        }

        const handleSaveOfComplexFields = () => {
            const list = fieldDescriptors?.map(d => [d.name, form.getFieldValue(d.name)])
            const savePromise = handleSave(record, { list })
            form.resetFields(fieldDescriptors?.map(d => d.name))
            return savePromise
        }

        const onBlur = async () => {
            const positionId = record?.documentId

            try {
                await form.validateFields()

                // Не await'им сам savePromise: форма одна на всю таблицу, и поле
                // этой колонки (например ['material', 'set']) не привязано к
                // конкретной позиции. Если держать ячейку смонтированной на время
                // сетевого запроса, клик по той же колонке в другой строке успеет
                // перезаписать это же поле раньше, чем текущее сохранение прочитает
                // значение — и обе ячейки уйдут в раунд взаимных blur/save. Поэтому
                // ячейка закрывается сразу же, как в исходном коде; savePromise
                // отслеживается только для Redux-состояния «идёт сохранение».
                const savePromise = fieldDescriptors
                    ? handleSaveOfComplexFields()
                    : handleSaveOfSimpleField()
                setEditing(false)

                if (positionId) {
                    void Promise.resolve(savePromise).finally(() => {
                        onEditingSessionChange?.(positionId, false)
                    })
                }
            } catch (e) {
                console.error(e)
                if (positionId) {
                    onEditingSessionChange?.(positionId, false)
                }
            }
        }

        const onCancel = () => {
            if (fieldDescriptors) {
                form.resetFields(fieldDescriptors?.map(d => d.name))
            } else {
                form.resetFields([getName()])
            }
            setEditing(false)
            if (record?.documentId) {
                onEditingSessionChange?.(record.documentId, false)
            }
        }

        const setSimpleFieldValue = () => {
            const name = getName()
            const value = getValue()

            form.setFieldValue(name, value)
        }

        const setComplexFieldsValue = () => {
            fieldDescriptors?.forEach(descriptor => {
                const name = descriptor.name
                const value = descriptor.getValue(record)

                form.setFieldValue(name, value)
            })
        }

        const onClickByCell = () => {
            console.log('onClickByCell')
            setEditing(true)
            if (record?.documentId) {
                onEditingSessionChange?.(record.documentId, true)
            }

            if (fieldDescriptors) {
                setComplexFieldsValue()
            } else {
                setSimpleFieldValue()
            }
        }

        let childNode = <>{children}</>

        if (editable) {
            childNode = editing ? (
                <EditableCellCtx.Provider
                    value={{
                        onBlur,
                        onCancel,
                    }}
                >
                    {React.cloneElement(FormItem as React.ReactElement<any>, { dataIndex, record })}
                </EditableCellCtx.Provider>
            ) : (
                <EditableCellValue onClick={onClickByCell}>
                    {/* <EditableCellValue style={{ paddingInlineEnd: 24 }} onClick={onClickByCell}> */}
                    {children}
                </EditableCellValue>
            )
        }

        childNode = (
            <ValidationTooltip message={validationResult?.message} editing={editing}>
                {childNode}
            </ValidationTooltip>
        )

        return (
            <TdStyled
                {...cellRest}
                style={mergedCellStyle}
                data-editing={editing}
                data-cell-validated={validationBg ? '1' : undefined}
                $textColor={validationResult?.color}
            >
                {childNode}
            </TdStyled>
        )
    }
)
